import { useIsPending } from '@veraclins-dev/react-utils';
import { Button, type ButtonProps } from '@veraclins-dev/ui/button';

import type { FormProps } from './form';

/**
 * Props for {@link FormSubmitButton}. Extends `ButtonProps` with the form's
 * `action` so the button can match {@link useIsPending} to its own submission
 * rather than any in-flight navigation.
 */
interface FormSubmitButtonProps
  extends ButtonProps, Pick<FormProps, 'action' | 'method'> {}

/**
 * Submit button that tracks pending state via {@link useIsPending} against
 * the matching `action`+`method` combination (`POST` by default). Use inside {@link Form}
 * (it's the default `actionButtons`) or anywhere you need a submit
 * button that only spins when its own form is in flight.
 *
 */
const FormSubmitButton = ({
  children = 'Submit',
  className,
  loading,
  id,
  action,
  method = 'post',
  ...props
}: FormSubmitButtonProps) => {
  const isPending = useIsPending({
    formAction: action,
    formMethod: method === 'get' ? 'GET' : 'POST',
  });

  const isLoading = loading || isPending;
  return (
    <Button
      id={id ?? ''}
      className={className}
      type="submit"
      loading={isLoading}
      emphasis="filled"
      tone="brand"
      {...props}
    >
      {children}
    </Button>
  );
};

export { FormSubmitButton, type FormSubmitButtonProps };
